import { useEffect, useRef } from 'react';
import { loadGooglePlaces } from '@/lib/googleMaps';

export default function PlaceSearchInput({ onPlaceSelected, value = '' }) {
    const containerRef = useRef(null);
    const onPlaceSelectedRef = useRef(onPlaceSelected);
    const autocompleteRef = useRef(null);

    useEffect(() => {
        onPlaceSelectedRef.current = onPlaceSelected;
    }, [onPlaceSelected]);

    useEffect(() => {
        if (autocompleteRef.current) {
            autocompleteRef.current.value = value;
        }
    }, [value]);

    useEffect(() => {
        let cancelled = false;
        const container = containerRef.current;

        async function initAutocomplete() {
            const placesLibrary = await loadGooglePlaces();

            if (cancelled || !container) {
                return;
            }

            const { PlaceAutocompleteElement } = placesLibrary;

            const autocomplete = new PlaceAutocompleteElement({
                includedRegionCodes: ['fr'],
            });

            autocompleteRef.current = autocomplete;

            autocomplete.placeholder = 'Rechercher un lieu...';
            autocomplete.style.width = '100%';
            autocomplete.style.height = '34px';
            autocomplete.classList.add('yonyon-place-autocomplete');
            autocomplete.addEventListener('focus', () => {
                if (autocomplete.matches(':focus')) {
                    autocomplete.style.border = 'inherit';
                }
            });

            autocomplete.addEventListener('gmp-select', async (event) => {
                const placePrediction = event.placePrediction;

                if (!placePrediction) {
                    return;
                }

                const place = placePrediction.toPlace();

                await place.fetchFields({
                    fields: ['displayName', 'formattedAddress', 'location'],
                });

                if (!place.location) {
                    return;
                }

                const label =
                    place.displayName && place.formattedAddress
                        ? `${place.displayName} — ${place.formattedAddress}`
                        : place.formattedAddress ||
                        place.displayName ||
                        'Lieu sélectionné';

                autocomplete.value = label;

                onPlaceSelectedRef.current?.({
                    label,
                    latitude: place.location.lat(),
                    longitude: place.location.lng(),
                });
            });

            container.replaceChildren(autocomplete);
        }

        initAutocomplete().catch((error) => {
            console.error('Impossible de charger Google Places', error);
        });

        return () => {
            cancelled = true;
            container?.replaceChildren();
            autocompleteRef.current = null;
        };
    }, []);

    return <div
        ref={containerRef}
        className="w-full rounded-md border border-background-200 text-sm text-foreground-900 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100"
    > </div>;
}